if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get variantIdSelect() {
        return this.querySelector('[name="id"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();
        this.select = this.querySelector('select');

        this.init();
        this.select.addEventListener('change', () => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.variantIdSelect.value;
          this.setCurrentVariantById(selectedVariantId);

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll('option');
                  options.forEach((option) => option.removeAttribute('selected'));

                  element.value = variantOptionVal;
                  const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                  if (selectedOption) {
                    selectedOption.setAttribute('selected', 'selected');
                  }
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
              this.isUpdating = false;
            }, 0);
          } else {
            this.isUpdating = false;
          }

          this.updatePrice();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            // PF guest restriction: if pfGuestDisabled is active for a logged-out guest,
            // keep the current disabled state of the add-to-cart button untouched.
            // Logged-in customers, and shops without pfGuestDisabled, always follow the
            // regular sold-out/availability logic from the selected variant.
            const pfGuestDisabled = window.britaMarketConfig?.pfGuestDisabled;
            const customerLoggedIn = window.britaMarketConfig?.customerLoggedIn;

            if (!pfGuestDisabled || customerLoggedIn) {
              this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
            }
          }
        });
      }

      getVariantData() {
        if (!this.variantData) {
          // Read variants from data attribute
          const variantsAttr = this.dataset.variants;
          if (variantsAttr) {
            this.variantData = JSON.parse(variantsAttr);
          }

          // Read product alternate image
          this.productAlternateImage = this.dataset.productAlternateImage || null;

          // Read variant alternate images and merge into variants
          const variantAlternateImagesAttr = this.dataset.variantAlternateImages;
          if (variantAlternateImagesAttr && this.variantData) {
            try {
              const alternateImages = JSON.parse(`{${variantAlternateImagesAttr}}`);
              this.variantData = this.variantData.map((variant) => ({
                ...variant,
                variantAlternateImage: alternateImages[variant.id] || null,
                productAlternateImage: this.productAlternateImage,
              }));
            } catch (e) {
              console.warn('Failed to parse variant alternate images:', e);
            }
          }
        }
        return this.variantData;
      }

      init() {
        [FoxTheme.config.mediaQueryMobile, FoxTheme.config.mediaQueryTablet].forEach((mediaQuery) => {
          const mql = window.matchMedia(mediaQuery);
          mql.onchange = this.checkDevice.bind(this);
        });
        this.checkDevice();

        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }
        this.productId = this.dataset.productId;

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.container.classList.toggle('sticky-atc-bar--show', !entry.isIntersecting);
            });
            this.checkDevice();
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);

        const initialVariantId = this.variantIdSelect?.value;
        this.setCurrentVariantById(initialVariantId);

        this.syncWithMainProductForm();
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      /**
       * Finds variant by ID and updates the current variant with image
       * @param {number|string} variantId - The variant ID to find
       * @returns {Object|null} - The found variant or null
       */
      setCurrentVariantById(variantId) {
        if (!variantId || !this.variantData) return null;

        const id = Number(variantId);
        this.currentVariant = this.variantData.find((v) => v.id === id) || this.variantData[0];

        if (this.currentVariant) {
          this.updateImage();
        }

        return this.currentVariant;
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      updateImage() {
        const imageWrapper = this.querySelector('.sticky-atc-bar__product-image');
        const img = imageWrapper?.querySelector('img');
        if (!img || !this.currentVariant) return;
        // Priority: variant alternate image > variant featured image > product alternate image > product featured image
        const newImageUrl =
          this.currentVariant.variantAlternateImage ||
          this.currentVariant.featured_image?.src ||
          this.currentVariant.productAlternateImage;

        if (newImageUrl) {
          // Update both src and srcset to ensure the image changes
          img.src = newImageUrl;
          img.srcset = newImageUrl;
          // Remove sizes attribute to prevent srcset from overriding
          img.removeAttribute('sizes');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          variantInput.value = variant.id;
          this.updatePrice();
          this.setCurrentVariantById(this.variantIdSelect?.value);
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            // PF guest restriction: if pfGuestDisabled is active for a logged-out guest,
            // keep the current disabled state of the add-to-cart button untouched.
            // Logged-in customers, and shops without pfGuestDisabled, always follow the
            // regular sold-out/availability logic from the fetched variant markup.
            const pfGuestDisabled = window.britaMarketConfig?.pfGuestDisabled;
            const customerLoggedIn = window.britaMarketConfig?.customerLoggedIn;

            if (!pfGuestDisabled || customerLoggedIn) {
              this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
            }
          }
        });
      }
    }
  );
}
